import { Component, OnInit } from '@angular/core';
import { FuiModal, FuiModalService } from 'ngx-fomantic-ui';
import { IFileManagerModalContext, IContextQueryResult, IListItemQueryResult, ICreateDirectoryModalContext, IUploadFilesCommand } from '../../models/interface';
import { DomSanitizer } from '@angular/platform-browser';
import { FileManagerService } from '../../services/file-manager.service';
import { finalize } from 'rxjs/operators';
import { Observable, forkJoin } from 'rxjs';
import { DndDropEvent } from 'ngx-drag-drop';
import { CreateDirectoryModal } from '../create-directory-modal/CreateDirectoryModal';
import { DialogService } from '../../../dialog/services/dialog.service';
import { AlertFacadeService } from '../../../alert-facade-service/services/alert-facade.service';

@Component({
  selector: 'ps-file-manager-modal',
  templateUrl: './file-manager-modal.component.html',
  styleUrls: ['./file-manager-modal.component.css']
})
export class FileManagerModalComponent implements OnInit {
  context: IFileManagerModalContext;
  items: IListItemQueryResult[] = [];
  isLoading: boolean = false;
  isUploading: boolean = false;

  selectedFiles: IListItemQueryResult[] = [];

  directoryHistory: IListItemQueryResult[] = [];

  storageContext: IContextQueryResult;
  selectedFile: any;

  get files() {
    return this.items.filter((item) => {
      if (!item.isDirectory) return true;
      return false;
    }).reverse();
  }

  get directories() {
    return this.items.filter((item) => {
      if (item.isDirectory) return true;
      return false;
    });
  }

  get hasFiles() {
    return this.files.length ? true : false;
  }

  //@ViewChild('fileInput', {static: false}) fileRef: ElementRef<HTMLInputElement>;

  constructor(public modal: FuiModal<IFileManagerModalContext, IListItemQueryResult|IListItemQueryResult[], void>,
              private sanitizer: DomSanitizer,
              private _fileManager: FileManagerService,
              private _modal: FuiModalService,
              private alertService: AlertFacadeService,
              private dialog: DialogService) { }

  ngOnInit() {
    this.context = this.modal.context;
    this.isLoading = true;

    forkJoin([
      this._fileManager.findContextByName(this.context.contextName), 
      this._fileManager.getItemsOfContext(this.context.contextName)
    ])
      .pipe(finalize(() => {
        this.isLoading = false;
      }))
      .subscribe((([context, items]) => {
        this.storageContext = context;
        this.items = items;
      }));
  }

  selectFile(file: IListItemQueryResult) {
    const index = this.selectedFiles.indexOf(file);
    if (index != -1) {
      this.selectedFiles.splice(index, 1);
      return;
    }
    
    if (!this.context.allowMultiSelection) {
      this.selectedFiles = [file];
      return;
    }

    this.selectedFiles.push(file);
  }

  onTravelRoot() {
    this.isLoading = true;

    this._fileManager.getItemsOfContext(this.context.contextName)
      .pipe(finalize(() => {
        this.isLoading = false;
      }))
      .subscribe((items) => {
        this.items = items;
        this.directoryHistory = [];
      });
  }
  

  isLastDirectory(directory: IListItemQueryResult) {
    return (this.directoryHistory.length - 1 == this.directoryHistory.indexOf(directory));
  }

  onDirectoryTravel(directory: IListItemQueryResult) {
    console.log(directory);
    this.travelDirectory(directory)
      .subscribe(() => {
        // only do this if we are successful into our getting previous directory
        this.directoryHistory.splice(this.directoryHistory.indexOf(directory) + 1);
      });
  }

  onRefresh() {
    if (!this.directoryHistory.length) {
      this.onTravelRoot();
      return;
    }

    this.isLoading = true;
    const directory = this.directoryHistory[this.directoryHistory.length - 1];
    this._fileManager.getItemsOfDirectory(directory.id)
      .pipe(finalize(() => {
        this.isLoading = false;
      }))
      .subscribe((items) => {
        this.items = items;
      });
  }

  getDirectoryFiles() {
    this.isLoading = true;
    this._fileManager.getItemsOfContext(this.context.contextName)
      .pipe(finalize(() => {
        this.isLoading = false;
      }))
      .subscribe((items) => {
        this.items = items;
      });
  }

  onDrop(event: DndDropEvent) {
    const data = event.event.dataTransfer;
    this.processFiles(data.files);
  }

  onFileInputChange(files: FileList) {
    this.processFiles(files);
  }
  
  onFileRemove(file: IListItemQueryResult) {
    this.dialog.confirmModal({
      header: 'Delete File',
      message: 'Are you sure you want to delete this item?'
    })
    .subscribe(() => {
      this.isLoading = true;
      this._fileManager.deleteFile(file.id)
        .pipe(finalize(() => {
          this.isLoading = false;
          this.selectedFiles.splice(this.selectedFiles.indexOf(file), 1);
        }))
        .subscribe((result) => {
          this.items.splice(this.items.indexOf(file), 1);
          console.log(result);
        }, (error) => {
          console.log(error);
        });
    }, (e) => {
        console.log('deletion cancelled', e)
    })
  }

  onDirectorySelected(directory: IListItemQueryResult) {
    this.travelDirectory(directory)
      .subscribe();
  }

  startUpload(form: any, fileInput: any) {
    form.reset();
    fileInput.click();
  }

  get fileName(): string {
    return this.selectedFile ? this.selectedFile.split('/').pop() : 'Select file';
  }

  private travelDirectory(directory: IListItemQueryResult) {
    return new Observable<IListItemQueryResult[]>((o) => {
      this.isLoading = true;
      this._fileManager.getItemsOfDirectory(directory.id)
        .pipe(finalize(() => {
          this.isLoading = false;
          o.complete();
        }))
        .subscribe((items) => {
          this.items = items;
          this.directoryHistory.push(directory);
          o.next(items);
        },
        (error) => {
          o.error(error);
        });
    });
  }

  // todo: completed icon
  getDeleteFileIcon(file: File) {
    let classes = "icon ";
    let spinner = "notched circle loading";
    let remove = "red trash alternate";
    let completed = "green check";

    if (this.isUploading) {
      classes += `${spinner}`;
    } else {
      classes += `${remove}`;
    }

    return classes;
  }

  onUpload(files: FileList) {
    this.isLoading = true;

    let command : IUploadFilesCommand = {
      contextName: this.context.contextName
    };

    if (this.directoryHistory.length) {
      command.directoryId = this.directoryHistory[this.directoryHistory.length - 1].id;
    }

    console.log(command, this.directoryHistory);

    let final:File[] = [];
    for(let i = 0; i < files.length; i++) {
      const file = files[i];
      final.push(file);
    }

    this._fileManager.uploadFiles(command, final)
      .pipe(finalize(() => {
        this.isLoading = false;
      }))
      .subscribe((response) => {
        for (let item of response) {
          if (item.success) {

            this._fileManager.findFileById(item.id).subscribe(file => {
              this.items.push(file);
            });

            this.alertService.showMessage({
              type: 'success',
              title: item.fileName,
              tapToDismiss: false,
              timeOut: 3000,
              position: "top right"
            })

          } else {
            this.alertService.showMessage({
              type: 'error',
              title: item.fileName,
              message: item.errorMessage,
              tapToDismiss: true,
              timeOut: null,
              position: "top right"
            })
          }
        }
      },
      (error) => {
        console.log(error);
      });
  }

  private processFiles(files: FileList) {
    this.onUpload(files);
  }

  isSelected(item: IListItemQueryResult) {
    return this.selectedFiles.indexOf(item) > -1;
  }

  onCreateFolder() {
    let modalContext : ICreateDirectoryModalContext = {
      context: this.storageContext
    };

    if (this.directoryHistory.length) {
      console.log(this.directoryHistory.length, this.directoryHistory);
      console.log(this.directoryHistory[this.directoryHistory.length - 1]);
      modalContext.directoryId = this.directoryHistory[this.directoryHistory.length - 1].id;
    }

    const ret = this._modal.open(new CreateDirectoryModal(modalContext))
    .onApprove((directory : IListItemQueryResult) => {
      console.log('approved', directory);
      this.items.push(directory);
    })
    .onDeny(() => {
      console.log('denied');
    });
  }

  isFileDisplayableMedia(file: IListItemQueryResult) {
    const list = [
      'jpg', 'jpeg', 'gif', 'png',
      'bmp', 'tif', 'tiff', 'eps' 
    ];
    let ext = file.extension.substr(1).toLowerCase();
    if (list.indexOf(ext) !== -1)
      return true;
    return false;
  }

  getFileDisplayIcon(file: IListItemQueryResult) {
    let icon = 'file';
    let ext = file.extension.substr(1);
    const list = {
      'file pdf': [
        'pdf'
      ],
      'file alternate': [
        'txt'
      ],
      'file powerpoint': [
        'pp', 'ppt', 'pptx'
      ],
      'file word': [
        'doc', 'docx'
      ],
      'file excel': [
        'xls', 'xlsx'
      ],
      'file audio': [
        'mp3'
      ],
      'file video': [
        'mp4'
      ],
      'file code': [
        'html', 'css', 'js'
      ],
      'file archive': [
        'zip'
      ],
      'file image': [
        'jpg', 'jpeg', 'gif', 'png',
        'bmp', 'tif', 'tiff', 'eps', 'webp' 
      ]
    }

    let keys = Object.keys(list);
    for (let i = 0; i < keys.length; i++) {
      if (list[keys[i]].indexOf(ext) == -1)
        continue;

      icon = keys[i];
      break;
    }

    return `${icon} icon massive`;
  }

  downloadFile(file: IListItemQueryResult) {
    let link = document.createElement("a");
    link.download = file.name;
    link.href = file.publicUrl;
    link.click();
  }

  // todo: support icons for other type of files instead of a image src
  getSource(file: File) {
    return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
  }

  onSubmit() {
    if (this.context.allowMultiSelection) {
      this.modal.approve(this.selectedFiles);
      return;
    }

    this.modal.approve(this.selectedFiles[0]);
  }

  get hasDirectories() {
    return this.directories.length  ? true : false;
  }

  get showDeleteDirectory() {
    return this.directoryHistory.length > 0 && this.hasDirectories == false && this.hasFiles == false;
  }

  deleteCurrentDirectory() {
    
    if (this.directoryHistory.length == 0)
      return;

      this.dialog.confirmModal({
        header: 'Delete Directory',
        message: 'Are you sure you want to delete this directory?'
      })
      .subscribe(() => {
        this.isLoading = true;
        const dirId = this.directoryHistory[this.directoryHistory.length - 1].id;
        this._fileManager.deleteDirectory(dirId)
          .pipe(finalize(() => {
            this.isLoading = false;
          }))
          .subscribe((result) => {
            this.onTravelRoot();
          }, (error) => {
            this.alertService.showMessage({
              type: 'error',
              position: 'bottom right',
              title: 'Failed to delete direcotry',
              message: 'Could not delete directory'
            })
          });
      }, (e) => {
          console.log('deletion cancelled', e)
      });
  }
}